import React, { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import styles from "./NetworkPage.module.css";
import TextHero from "../../COMPONENTS/TEXT_HERO";
import Portrait from "../../COMPONENTS/PORTRAIT";
import { Section } from "../../COMPONENTS/UI";
import FinalCta from "../HOME_PAGE/SECTIONS/CTA";
import { investigatorsList } from "../../CONSTANTS/investigatorsList";
import useDocumentMeta from "../../UTILS/HOOKS/useDocumentMeta";
import { EASE } from "../../UTILS/ANIMATION/motionVariants";

const asList = (v) =>
  (Array.isArray(v) ? v : typeof v === "string" ? v.split(",") : []).map((s) => s.trim()).filter(Boolean);
const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const num = (v) => parseInt(String(v || "").match(/\d+/)?.[0] || "0", 10);
const placeOf = (d) => `${titleCase(d.location.city)}, ${titleCase(d.location.state)}`;

// Most trial experience first, then research years, then name.
const byExperience = (a, b) =>
  num(b.numberOfClinicalTrail) - num(a.numberOfClinicalTrail) ||
  num(b.yearsResearchClinicalTrail) - num(a.yearsResearchClinicalTrail) ||
  a.name.localeCompare(b.name);

const doctors = [...investigatorsList].sort(byExperience);

const countBy = (keysOf) => {
  const counts = new Map();
  doctors.forEach((d) => keysOf(d).forEach((k) => counts.set(k, (counts.get(k) || 0) + 1)));
  return counts;
};
const locationCounts = countBy((d) => [placeOf(d)]);
const specialtyCounts = countBy((d) => asList(d.TheraupaticalArea));

// Locations grouped by state, cities alphabetical.
const locationOptions = [...locationCounts.keys()].sort((a, b) => {
  const [ca, sa] = a.split(", ");
  const [cb, sb] = b.split(", ");
  return sa.localeCompare(sb) || ca.localeCompare(cb);
});
const specialtyOptions = [...specialtyCounts.keys()].sort((a, b) => a.localeCompare(b));

const VIEWS = {
  doctors: {
    label: "Doctors",
    title: (
      <>
        Meet the <em>KUR investigators.</em>
      </>
    ),
    lead: "Experienced, multi-specialty physicians who bring clinical research to patients in their own communities.",
  },
  locations: {
    label: "Locations",
    title: (
      <>
        Investigators <em>by location.</em>
      </>
    ),
    lead: "Find the KUR investigators at each of our clinical research sites.",
  },
  "therapeutic-areas": {
    label: "Therapeutic areas",
    title: (
      <>
        Investigators by <em>therapeutic area.</em>
      </>
    ),
    lead: "Browse the KUR investigator network by specialty.",
  },
};

const findOption = (options, value) =>
  value ? options.find((o) => o.toLowerCase() === value.toLowerCase()) : undefined;

const InvestigatorTile = ({ doc }) => {
  const trials = num(doc.numberOfClinicalTrail);
  const years = String(doc.yearsResearchClinicalTrail || "").trim();
  return (
    <article className={styles.card}>
      <Portrait src={doc.image} alt={doc.name} name={doc.name} shape="arch" className={styles.portrait} />
      <h3 className={styles.name}>{doc.name}</h3>
      <p className={styles.specialty}>{asList(doc.specialization).join(", ")}</p>
      <p className={styles.place}>{placeOf(doc)}</p>
      {(trials > 0 || years) && (
        <dl className={styles.stats}>
          {trials > 0 && (
            <div>
              <dt>Trials</dt>
              <dd>{trials}+</dd>
            </div>
          )}
          {years && (
            <div>
              <dt>Research</dt>
              <dd>{years.replace(/\s*yrs?$/i, "")} yrs</dd>
            </div>
          )}
        </dl>
      )}
    </article>
  );
};

const NetworkPage = () => {
  const { section } = useParams();
  const view = VIEWS[section] ? section : "doctors";
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const reduce = useReducedMotion();

  // Deep links such as ?city=Miami&state=Florida or ?specialty=cardiology pick
  // the starting filter; after that the choice is local, so the page does not
  // jump back to the top on every click.
  const initialLocation =
    findOption(locationOptions, query.get("city") && `${query.get("city")}, ${query.get("state")}`) ||
    locationOptions[0];
  const initialSpecialty = findOption(specialtyOptions, query.get("specialty")) || specialtyOptions[0];
  const [location, setLocation] = useState(initialLocation);
  const [specialty, setSpecialty] = useState(initialSpecialty);
  const [term, setTerm] = useState("");

  useDocumentMeta({
    title: `${VIEWS[view].label} | KUR Research Network`,
    description: VIEWS[view].lead,
    path: `/network/${view}`,
  });

  const results = useMemo(() => {
    if (view === "locations") return doctors.filter((d) => placeOf(d) === location);
    if (view === "therapeutic-areas") return doctors.filter((d) => asList(d.TheraupaticalArea).includes(specialty));
    const t = term.trim().toLowerCase();
    if (!t) return doctors;
    return doctors.filter((d) =>
      [d.name, placeOf(d), ...asList(d.specialization)].some((s) => s.toLowerCase().includes(t))
    );
  }, [view, location, specialty, term]);

  const options = view === "locations" ? locationOptions : specialtyOptions;
  const counts = view === "locations" ? locationCounts : specialtyCounts;
  const selected = view === "locations" ? location : specialty;
  const select = view === "locations" ? setLocation : setSpecialty;
  const scope = view === "doctors" ? (term.trim() ? `matching “${term.trim()}”` : "in our network directory") : `in ${selected}`;

  return (
    <>
      <TextHero id="network-hero-title" title={VIEWS[view].title} lead={VIEWS[view].lead}>
        <div className={styles.heroMeta}>
          <nav className={styles.views} aria-label="Browse the network">
            {Object.entries(VIEWS).map(([key, v]) => (
              <Link
                key={key}
                to={`/network/${key}`}
                className={key === view ? styles.viewActive : styles.view}
                aria-current={key === view ? "page" : undefined}
              >
                {v.label}
              </Link>
            ))}
          </nav>
        </div>
      </TextHero>

      <Section tone="light" id="investigators" aria-label="Investigators">
        <div className={styles.toolbar}>
          {view === "doctors" ? (
            <label className={styles.search}>
              <span className={styles.srOnly}>Search investigators</span>
              <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.searchIcon}>
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search by name, specialty or city"
              />
            </label>
          ) : (
            <div className={styles.chips} role="group" aria-label={`Filter by ${VIEWS[view].label.toLowerCase()}`}>
              {options.map((o) => (
                <button
                  key={o}
                  type="button"
                  className={o === selected ? styles.chipActive : styles.chip}
                  aria-pressed={o === selected}
                  onClick={() => select(o)}
                >
                  {o}
                  <span className={styles.count}>{counts.get(o)}</span>
                </button>
              ))}
            </div>
          )}
          <p className={styles.summary} aria-live="polite">
            <strong>{results.length}</strong> {results.length === 1 ? "investigator" : "investigators"} {scope}
          </p>
        </div>

        {results.length === 0 ? (
          <p className={styles.empty}>No investigators match that search.</p>
        ) : (
          <motion.ul className={styles.grid} layout={!reduce}>
            <AnimatePresence mode="popLayout" initial={false}>
              {results.map((doc, i) => (
                <motion.li
                  key={doc.name}
                  layout={!reduce}
                  initial={reduce ? false : { opacity: 0, y: 24, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={reduce ? undefined : { opacity: 0, scale: 0.96, transition: { duration: 0.2 } }}
                  transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.04, ease: EASE }}
                >
                  <InvestigatorTile doc={doc} />
                </motion.li>
              ))}
            </AnimatePresence>
          </motion.ul>
        )}
      </Section>

      <FinalCta />
    </>
  );
};

export default NetworkPage;
